import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import NotesRoundedIcon from '@mui/icons-material/NotesRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import FlagRoundedIcon from '@mui/icons-material/FlagRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import { motion } from 'framer-motion'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useTimeline } from '../../context/useTimeline'
import { useWorkspace } from '../../context/useWorkspace'
import { useToast } from '../../context/useToast'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import { applyStripboardDelta, ChecklistBlockerError } from '../../services/stripboardDeltaService'
import { updateChecklistWorkflowStatus } from '../../services/checklistStatusWriter'
import { subscribeWorkspaceUserDirectory, type WorkspaceUserDirectoryEntry } from '../../services/workspaceUserDirectory'
import type { ChecklistItem, ChecklistWorkflowStatus } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import type { BlockerCtaSection } from '../../types/checklistBlockerCodes'
import { useMemberBoard } from '../../member/useMemberBoard'
import { isChecklistGate } from '../../utils/checklistDependencyState'
import type { WorkspaceAction } from '../../utils/workspacePermissions'
import {
  CHECKLIST_WORKFLOW_STATUSES,
  filterChecklistItemsForStripboard,
  getMemberBoardCardTitle,
  getLocalizedMemberBoardStatusFilterMeta,
  isSyntheticDashboardChecklistParent,
  normalizeStripboardStatus,
  type MemberBoardStatusFilter,
} from './memberBoardUtils'
import { useNls } from '../../nls/nlsProvider'
import { getMemberStatusLabel } from '../Mobile/MobileNls'
import { MemberBottomSheet } from './controls/MemberControlsPrimitives'
import { Pressable } from '../Common/Pressable'
import { StatusChip } from '../Common'
import { MemberNexusHero } from './controls/MemberNexusPrimitives'
import { DropdownMenu, type DropdownMenuItem } from '../Common/DropdownMenu'
import { useMemberLayout } from './MemberLayoutContext'
import { isTimesheetsEnabled, isTimesheetsTierEligible } from '../../services/featureRegistry'
import { ItemTimeDisplay } from '../Timesheets/ItemTimeDisplay'
import { ItemTimeProvider, useItemTime } from '../Timesheets/ItemTimeProvider'
import './memberBoardConsistency.css'

const CHECKLIST_STATUSES = CHECKLIST_WORKFLOW_STATUSES
const STRIPBOARD_FILTER_IDS: MemberBoardStatusFilter[] = ['ALL', ...CHECKLIST_STATUSES.map((status) => status.id)]

const formatDate = (value?: string | null): string | null => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const canChangeChecklistStatus = (activeRole: string, canPerform: (action: WorkspaceAction) => boolean) =>
  activeRole !== 'VIEWER' && canPerform('checklist.change_status')

const resolveAssigneeName = (
  assigneeId?: string | null,
  rawName?: string | null,
  memberNames?: Map<string, string>,
  actorUid?: string | null,
  actorDisplayName?: string | null,
): string | null => {
  if (assigneeId && memberNames?.has(assigneeId)) {
    return memberNames.get(assigneeId) ?? null
  }
  if (assigneeId && actorUid && assigneeId === actorUid && actorDisplayName) {
    return actorDisplayName
  }
  if (rawName) {
    const trimmed = rawName.trim()
    if (trimmed.includes('@')) {
      return trimmed.split('@')[0] || null
    }
    return trimmed || null
  }
  return null
}

const MemberChecklistStripCard = ({
  item,
  parent,
  status,
  canMutate,
  isFailed,
  isHighlighted,
  onOpen,
  onOpenSection,
  canManageFieldPasses,
  activeFilter,
  onAccept,
  memberNames,
  actorUid,
  actorDisplayName,
}: {
  item: ChecklistItem
  parent: TimelineElement | null
  status: ChecklistWorkflowStatus
  canMutate: boolean
  isFailed: boolean
  isHighlighted: boolean
  onOpen: () => void
  onOpenSection: (section: BlockerCtaSection) => void
  canManageFieldPasses: boolean
  activeFilter: MemberBoardStatusFilter
  onAccept: (item: ChecklistItem) => void
  memberNames?: Map<string, string>
  actorUid?: string | null
  actorDisplayName?: string | null
}) => {
  const { t } = useNls()
  const due = formatDate(item.scheduleEndDate ?? item.endDate ?? item.scheduleStartDate ?? item.startDate)
  const title = getMemberBoardCardTitle(item)
  const isBoardLevel = isSyntheticDashboardChecklistParent(item.parentContainerId)
  const parentLabel = parent?.title ?? (isBoardLevel ? t('NLSMemberChecklistBoardParent') : t('NLSMemberChecklistWorkspaceParent'))
  const isRecurring = item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY'
  const eyebrow = isRecurring
    ? (item.checklistType === 'DAILY' ? t('NLSMemberBoardDailyChecklist') : t('NLSMemberBoardWeeklyChecklist'))
    : null
  const proofPending = Boolean(item.requiresProofFile && !item.proofFileUrl && !item.proofSubmissionId)
  const locationPending = Boolean(item.requiresLocationPin && !item.verifiedCoordinates)
  const reviewPending = status === 'ASSESSING' || item.isPendingReview === true
  const assigneeDisplayName = resolveAssigneeName(item.assigneeId, item.assigneeName, memberNames, actorUid, actorDisplayName)
  const { activeWorkspaceId, workspaces = [] } = useWorkspace()
  const targetWsId = item.workspaceId || activeWorkspaceId
  const workspace = (workspaces ?? []).find((w) => w.id === targetWsId) ?? null
  const timesheetsAllowed = Boolean(
    workspace && isTimesheetsEnabled(workspace) && isTimesheetsTierEligible(workspace.plan, workspace),
  )
  const { isRunningHere, start: startItemTimer, stop: stopItemTimer } = useItemTime(timesheetsAllowed ? item.id : null)

  const copyLink = useCallback(async () => {
    const url = new URL(window.location.href)
    url.searchParams.set('checklistItem', item.id)
    await navigator.clipboard?.writeText(url.toString())
  }, [item.id])

  const menuItems = useMemo<DropdownMenuItem[]>(() => {
    const actions: DropdownMenuItem[] = []

    if (activeFilter === 'ASSIGNED' && canMutate) {
      actions.push({
        key: 'accept',
        label: t('NLSMemberBoardActionAccept'),
        icon: <TaskAltRoundedIcon fontSize="small" />,
        onSelect: () => onAccept(item),
      })
    }

    actions.push(
      {
        key: 'open-detail',
        label: t('NLSMemberBoardActionOpenDetail'),
        icon: <OpenInNewRoundedIcon fontSize="small" />,
        onSelect: onOpen,
      },
      {
        key: 'copy-link',
        label: t('NLSMemberBoardActionCopyLink'),
        icon: <ContentCopyRoundedIcon fontSize="small" />,
        onSelect: () => {
          void copyLink()
        },
      },
    )

    const jumpItems: DropdownMenuItem[] = [
      {
        key: 'jump-description',
        label: t('NLSMemberBoardActionDescription'),
        icon: <NotesRoundedIcon fontSize="small" />,
        onSelect: () => onOpenSection('description'),
      },
      {
        key: 'jump-assign',
        label: item.assigneeId ? t('NLSMemberBoardActionReassign') : t('NLSMemberBoardActionAssign'),
        icon: <PersonRoundedIcon fontSize="small" />,
        onSelect: () => onOpenSection('assignee'),
      },
    ]

    if (item.requiresProofFile) {
      jumpItems.push({
        key: 'jump-proof',
        label: t('NLSMemberBoardActionProof'),
        icon: <AttachFileRoundedIcon fontSize="small" />,
        onSelect: () => onOpenSection('proof'),
      })
    }

    jumpItems.push({
      key: 'jump-schedule',
      label: t('NLSMemberBoardActionDates'),
      icon: <ScheduleRoundedIcon fontSize="small" />,
      onSelect: () => onOpenSection('dates'),
    })

    const hasResources = Boolean(
      item.resourceReadySku ||
      item.bindings?.budgetBinding ||
      (typeof item.estimatedHours === 'number' && item.estimatedHours > 0) ||
      (typeof item.estimatedCost === 'number' && item.estimatedCost > 0) ||
      (typeof item.actualCost === 'number' && item.actualCost > 0),
    )
    if (hasResources) {
      jumpItems.push({
        key: 'jump-resources',
        label: t('NLSMemberBoardActionResources'),
        icon: <InventoryRoundedIcon fontSize="small" />,
        onSelect: () => onOpenSection('resources'),
      })
    }

    if (timesheetsAllowed) {
      jumpItems.push({
        key: 'jump-timer',
        label: isRunningHere
          ? t('NLSTimesheetsItemStopTimer')
          : t('NLSTimesheetsItemStartTimer'),
        icon: isRunningHere ? <StopRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />,
        onSelect: async () => {
          if (isRunningHere) {
            await stopItemTimer()
          } else {
            await startItemTimer()
          }
        },
      })
    }

    if (item.completionCriteria && item.completionCriteria.trim().length > 0) {
      jumpItems.push({
        key: 'jump-completion-criteria',
        label: t('NLSMemberBoardActionCompletionCriteria'),
        icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
        onSelect: () => onOpenSection('completionCriteria'),
      })
    }

    jumpItems.push({
      key: 'jump-signals',
      label: t('NLSMemberBoardActionSignals'),
      icon: <FlagRoundedIcon fontSize="small" />,
      onSelect: () => onOpenSection('signals'),
    })

    if (canManageFieldPasses) {
      jumpItems.push({
        key: 'jump-field-pass',
        label: t('NLSMemberBoardActionFieldPass'),
        icon: <BadgeRoundedIcon fontSize="small" />,
        onSelect: () => onOpenSection('fieldPass'),
      })
    }

    if (item.attachedFormIds && item.attachedFormIds.length > 0) {
      jumpItems.push({
        key: 'jump-attached-forms',
        label: t('NLSMemberBoardActionAttachedForms'),
        icon: <DescriptionRoundedIcon fontSize="small" />,
        onSelect: () => onOpenSection('attachedForms'),
      })
    }

    if (jumpItems.length > 0) {
      jumpItems[0].dividerBefore = true
      jumpItems[0].groupHeading = t('NLSMemberBoardActionGroupJumpTo')
    }

    return [...actions, ...jumpItems]
  }, [
    activeFilter,
    canManageFieldPasses,
    canMutate,
    copyLink,
    isRunningHere,
    item,
    onAccept,
    onOpen,
    onOpenSection,
    startItemTimer,
    stopItemTimer,
    t,
    timesheetsAllowed,
  ])

  return (
    <motion.article
      className={`memberBoardCard memberChecklistStripCard ${!canMutate ? 'isReadOnly' : ''} ${isFailed ? 'isFailed' : ''}`}
      data-member-board-card={item.id}
      data-status={status.toLowerCase()}
      onClick={onOpen}
      animate={isHighlighted ? {
        boxShadow: [
          '0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent)',
          '0 0 0 2px color-mix(in srgb, var(--accent) 75%, transparent)',
          '0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent)',
        ],
      } : undefined}
      transition={{ duration: 1.05, ease: 'easeOut' }}
    >
      <span className={`memberChecklistStripAccent memberChecklistStripAccent--${status.toLowerCase()}`} aria-hidden="true" />
      <header>
        <span className="memberChecklistStripIcon" aria-hidden="true">
          <ChecklistRoundedIcon fontSize="small" />
        </span>
        <div className="memberChecklistStripHeaderCopy">
          <strong>{title}</strong>
          {(parentLabel || due || eyebrow) ? (
            <span className="memberChecklistStripSubtitle">
              {parentLabel ? <span>{parentLabel}</span> : null}
              {due ? <span>{due}</span> : null}
              {eyebrow ? <span>{eyebrow}</span> : null}
            </span>
          ) : null}
        </div>
        <div className="memberChecklistStripHeaderActions">
          {isFailed ? <ErrorOutlineRoundedIcon className="memberBoardErrorIcon" fontSize="small" /> : null}
          <DropdownMenu
            placement="bottom-end"
            ariaLabel={t('NLSMemberBoardActionMenuAria', { title })}
            trigger={({ open, toggle, anchorRef }) => (
              <Pressable
                ref={anchorRef}
                className="memberBoardOverflowTrigger"
                aria-label={t('NLSMemberBoardActionMenuAria', { title })}
                aria-haspopup="menu"
                aria-expanded={open}
                title={t('NLSMemberBoardActionMenuTitle')}
                onPointerDown={(event) => {
                  event.stopPropagation()
                }}
                onClick={(event) => {
                  event.stopPropagation()
                  toggle()
                }}
              >
                <MoreVertRoundedIcon fontSize="small" />
              </Pressable>
            )}
            items={menuItems}
          />
        </div>
      </header>
      <p>{item.description || item.completionCriteria || t('NLSMemberBoardNoDescription')}</p>
      <div className="memberChecklistStripSignals" aria-label={t('NLSMemberBoardRequirementsAria')}>
        {isBoardLevel ? <StatusChip size="sm" tone="neutral" className="memberChecklistStripMetaChip">{t('NLSMemberBoardRoot')}</StatusChip> : null}
        {proofPending ? <span className="memberChecklistStripSignal">{t('NLSMemberBoardProofNeeded')}</span> : null}
        {locationPending ? <span className="memberChecklistStripSignal">{t('NLSMemberBoardLocationNeeded')}</span> : null}
        {reviewPending ? <span className="memberChecklistStripSignal">{t('NLSMemberBoardReviewPending')}</span> : null}
        <ItemTimeDisplay
          workspaceId={targetWsId}
          nodeId={item.id}
          estimatedHours={item.estimatedHours}
          actualMinutes={item.actualMinutes}
        />
      </div>
      <footer>
        <span className="memberChecklistStripStatus">{getMemberStatusLabel(status, t)}</span>
        {assigneeDisplayName ? <span className="memberChecklistStripTinyMeta">{assigneeDisplayName}</span> : null}
      </footer>
    </motion.article>
  )
}

export const MemberBoard = ({
  highlightedNodeId,
  onOpenElement,
  onOpenChecklistItem,
}: {
  highlightedNodeId: string | null
  onOpenElement: (element: TimelineElement) => void
  onOpenChecklistItem?: (item: ChecklistItem, opts?: { pendingStatus?: ChecklistWorkflowStatus; targetSection?: BlockerCtaSection }) => void
}) => {
  const { t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const { checklistItems, isSyncing, failedElementIds } = useMemberBoard()
  const { elements, canPerformOnNode, pathOnlyElementIds = new Set(), isAccessRestricted = false } = useTimeline()
  const { activeWorkspaceId, activeRole, canPerform } = useWorkspace()
  const { todayIso, weekKey } = useWorkspaceDay(activeWorkspaceId)
  const { showToast } = useToast()
  const memberLayout = useMemberLayout()
  const isInitialFilterMount = useRef(true)
  const [activeStatus, setActiveStatus] = useState<MemberBoardStatusFilter>('ALL')
  const [statusSheetOpen, setStatusSheetOpen] = useState(false)

  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false
      return
    }
    memberLayout?.scrollStageToTop()
  }, [activeStatus, memberLayout])
  const [directoryMembers, setDirectoryMembers] = useState<WorkspaceUserDirectoryEntry[]>([])
  const highlightedOnceRef = useRef<string | null>(null)
  const canMutate = canChangeChecklistStatus(activeRole, canPerform)
  const canManageFieldPasses = canPerform('workspace.manage_field_passes')
  const actor = useMemo(
    () => (currentUser ? { uid: currentUser.uid, role: activeRole, name: displayName || currentUser.email || null } : null),
    [activeRole, currentUser, displayName],
  )

  useEffect(() => {
    if (!activeWorkspaceId) return
    return subscribeWorkspaceUserDirectory(activeWorkspaceId, setDirectoryMembers)
  }, [activeWorkspaceId])

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>()
    directoryMembers.forEach((m) => {
      if (m.uid && m.displayName) map.set(m.uid, m.displayName)
    })
    return map
  }, [directoryMembers])

  useEffect(() => {
    if (!highlightedNodeId || highlightedOnceRef.current === highlightedNodeId) return
    highlightedOnceRef.current = highlightedNodeId
    window.setTimeout(() => {
      document.querySelector(`[data-member-board-card="${highlightedNodeId}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })
    }, 80)
  }, [highlightedNodeId])

  const parentById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])
  const dashboardChecklistItems = useMemo(
    () => filterChecklistItemsForStripboard(
      checklistItems.filter((item) => !isChecklistGate(item)),
      activeWorkspaceId,
      'ALL',
    ),
    [activeWorkspaceId, checklistItems],
  )
  const countsByStatus = useMemo(() => {
    const next = new Map<MemberBoardStatusFilter, number>()
    next.set('ALL', dashboardChecklistItems.length)
    CHECKLIST_STATUSES.forEach((status) => next.set(status.id, 0))
    dashboardChecklistItems.forEach((item) => {
      const status = normalizeStripboardStatus(item)
      next.set(status, (next.get(status) ?? 0) + 1)
    })
    return next
  }, [dashboardChecklistItems])
  const visibleItems = useMemo(
    () => filterChecklistItemsForStripboard(dashboardChecklistItems, activeWorkspaceId, activeStatus),
    [activeStatus, activeWorkspaceId, dashboardChecklistItems],
  )
  const stripboardFilters = useMemo(
    () => STRIPBOARD_FILTER_IDS.map((id) => ({ id, ...getLocalizedMemberBoardStatusFilterMeta(id, t) })),
    [t],
  )
  const activeMeta = stripboardFilters.find((status) => status.id === activeStatus) ?? stripboardFilters[0]

  const acceptAssignedItem = async (item: ChecklistItem) => {
    if (!canMutate || !canPerformOnNode('checklist.change_status', item.id) || !actor || normalizeStripboardStatus(item) !== 'ASSIGNED') return
    try {
      const isRepeatingChecklist = item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY'
      const periodKey = isRepeatingChecklist
        ? (item.checklistType === 'WEEKLY' ? weekKey : todayIso)
        : null
      await updateChecklistWorkflowStatus({
        item: {
          id: item.id,
          boardStatus: item.boardStatus,
          recordVersion: item.recordVersion,
          workflowStatus: normalizeStripboardStatus(item),
          workspaceId: item.workspaceId,
          checklistType: item.checklistType,
        },
        workflowStatus: 'ACCEPTED',
        periodKey,
        actor: { id: actor.uid, name: actor.name ?? '' },
        runDelta: applyStripboardDelta,
      })
      showToast('Checklist accepted.', 'success')
    } catch (error) {
      if (error instanceof ChecklistBlockerError) {
        const url = new URL(window.location.href)
        url.searchParams.set('checklistItem', item.id)
        await navigator.clipboard?.writeText(url.toString())
        showToast("You can't do that. Checklist link copied.", 'warning')
        return
      }
      showToast(error instanceof Error ? error.message : t('NLSMemberBoardToastStatusFailed'), 'warning')
    }
  }

  const activeCount = countsByStatus.get(activeStatus) ?? 0

  const filterBar = (
    <div className="memberBoardFilterBar">
      <Pressable
        className="memberBoardStatusChip"
        onClick={() => setStatusSheetOpen(true)}
        aria-label={`${t('NLSMemberBoardAriaStatuses')}: ${activeMeta.label} (${activeCount})`}
        data-testid="member-board-status-filter-chip"
      >
        <span className="memberBoardStatusChip__label">
          <span>{activeMeta.label}</span>
          <strong className="memberBoardStatusChip__count">{activeCount}</strong>
        </span>
        <KeyboardArrowDownRoundedIcon className="memberBoardStatusChip__chevron" aria-hidden="true" />
      </Pressable>
      <MemberBottomSheet
        open={statusSheetOpen}
        onClose={() => setStatusSheetOpen(false)}
        title={t('NLSMemberBoardAriaStatuses')}
        maxHeightSvh={80}
        testId="member-board-status-sheet"
      >
        <div className="memberBoardStatusSheet">
          {stripboardFilters.map((status) => {
            const isSelected = status.id === activeStatus
            const count = countsByStatus.get(status.id) ?? 0
            return (
              <Pressable
                key={status.id}
                className={`memberBoardStatusSheet__option${isSelected ? ' is-selected' : ''}`}
                aria-pressed={isSelected}
                onClick={() => {
                  if (status.id !== activeStatus) {
                    setActiveStatus(status.id)
                  }
                  setStatusSheetOpen(false)
                }}
                data-testid={`member-board-status-option-${status.id.toLowerCase()}`}
              >
                <span className="memberBoardStatusSheet__optionLabel">
                  <span>{status.label}</span>
                  <strong className="memberBoardStatusSheet__optionCount">{count}</strong>
                </span>
                {isSelected ? <CheckRoundedIcon className="memberBoardStatusSheet__check" aria-hidden="true" /> : null}
              </Pressable>
            )
          })}
        </div>
      </MemberBottomSheet>
    </div>
  )

  if (isAccessRestricted && elements.length === 0) {
    return (
      <section className="memberBoardShell" aria-label={t('NLSMemberBoardAriaStripboard')}>
        <div className="grid min-h-80 place-items-center gap-1 text-center p-8">
          <p className="text-sm font-medium text-[var(--muted)]">{t('NLSScopeEmptyRestricted')}</p>
        </div>
      </section>
    )
  }

  return (
    <ItemTimeProvider workspaceId={activeWorkspaceId}>
      <section className="memberBoardShell" aria-label={t('NLSMemberBoardAriaStripboard')}>
        <MemberNexusHero
          eyebrow={t('NLSMemberBoardEyebrow')}
          title={t('NLSMemberBoardTitle')}
          className="memberBoardHero"
          rightSlot={
            <div className="memberBoardSyncCluster">
              {isSyncing ? <StatusChip size="sm" tone="info" className="memberBoardSyncBadge">{t('NLSMemberBoardSyncing')}</StatusChip> : <StatusChip size="sm" tone="success" className="memberReadOnlyBadge">{t('NLSMemberBoardSynced')}</StatusChip>}
              {!canMutate ? <StatusChip size="sm" tone="muted" className="memberReadOnlyBadge">{t('NLSMemberBoardViewOnly')}</StatusChip> : null}
            </div>
          }
        />
        {memberLayout?.bottomAddonTarget ? createPortal(filterBar, memberLayout.bottomAddonTarget) : filterBar}
        <section className="memberBoardColumn memberChecklistStatusColumn">
          <header>
            <div>
              <h3>{activeMeta.label}</h3>
              <p>{activeMeta.hint}</p>
            </div>
            <strong className="memberBoardSectionCount">{visibleItems.length}</strong>
          </header>
          <div className="memberBoardDropZone" aria-label={t('NLSMemberBoardAriaItemsInStatus', { label: activeMeta.label })}>
            {visibleItems.length ? visibleItems.map((item) => (
              <MemberChecklistStripCard
                key={item.id}
                item={item}
                parent={item.parentContainerId ? parentById.get(item.parentContainerId) ?? null : null}
                status={normalizeStripboardStatus(item)}
                canMutate={canMutate && canPerformOnNode('checklist.change_status', item.id)}
                isFailed={failedElementIds.has(item.id)}
                isHighlighted={highlightedNodeId === item.id}
                canManageFieldPasses={canManageFieldPasses}
                onOpen={() => {
                  const parent = item.parentContainerId ? parentById.get(item.parentContainerId) ?? null : null
                  if (parent && !pathOnlyElementIds.has(parent.id)) onOpenElement(parent)
                  onOpenChecklistItem?.(item)
                }}
                onOpenSection={(section) => {
                  const parent = item.parentContainerId ? parentById.get(item.parentContainerId) ?? null : null
                  if (parent && !pathOnlyElementIds.has(parent.id)) onOpenElement(parent)
                  onOpenChecklistItem?.(item, { targetSection: section })
                }}
                activeFilter={activeStatus}
                onAccept={(acceptedItem) => void acceptAssignedItem(acceptedItem)}
                memberNames={memberNameById}
                actorUid={actor?.uid}
                actorDisplayName={displayName}
              />
            )) : <p className="memberBoardEmpty">{t('NLSMemberBoardEmptyStatus', { label: activeMeta.label.toLowerCase() })}</p>}
          </div>
        </section>
      </section>
    </ItemTimeProvider>
  )
}
